/**
 * RunHistorySidebar.tsx
 *
 * Collapsible left panel listing past runs for the current tenant.
 * Clicking a run card updates the application's active run ID and
 * marks the view as read-only (the ReviewSidebar hides action buttons).
 *
 * Layout (when open, 268 px wide):
 *   ┌─────────────────────────────┐
 *   │ ◀  Run History              │  ← collapse toggle
 *   ├─────────────────────────────┤
 *   │ [Status filter ▾]           │
 *   ├─────────────────────────────┤
 *   │ ● lead-gen  · 2m ago        │  ← run card
 *   │   ✓ Succeeded               │
 *   ├─────────────────────────────┤
 *   │ ● market-research · 1h ago  │
 *   │   ✗ Failed                  │
 *   ├─────────────────────────────┤
 *   │      [ Load more ]          │
 *   └─────────────────────────────┘
 *
 * When collapsed: 40 px strip showing a ▶ toggle button only.
 *
 * Data source: GET /v1/runs?limit=25&cursor=...
 * Cached in useObservabilityStore.historyRuns so switching between
 * Live and History views is instantaneous.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react'
import { useObservabilityStore } from '../store/useObservabilityStore'
import type { RunRecord } from '../store/useObservabilityStore'
import { api } from '../api/client'

// ── Helpers ───────────────────────────────────────────────────────────────────

function primarySkill(run: RunRecord): string {
  const ip = run.inputPayload
  if (!ip) return shortId(run.id)
  // Most skill inputs carry `skill_id` or `skillId` at the top level.
  const raw = (ip['skill_id'] ?? ip['skillId'] ?? '') as string
  if (raw) return raw.replace(/-/g, ' ')
  return shortId(run.id)
}

function shortId(id: string): string {
  return id.slice(0, 8)
}

// Absolute, compact date for a run, e.g. "2 Jun 12:34".
function formatRunDate(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
    })
  } catch {
    return '—'
  }
}

// ── Status badge ──────────────────────────────────────────────────────────────

type BadgeStatus = 'succeeded' | 'failed' | 'running' | 'waiting' | string

function StatusBadge({ status }: { status: BadgeStatus }) {
  const cfg: Record<string, { label: string; color: string }> = {
    succeeded: { label: 'Succeeded', color: '#4ade80' },
    failed:    { label: 'Failed',    color: '#f87171' },
    running:   { label: 'Running',   color: '#38bdf8' },
    waiting:   { label: 'Waiting',   color: '#fbbf24' },
    cancelled: { label: 'Cancelled', color: '#94a3b8' },
    queued:    { label: 'Queued',    color: '#a78bfa' },
  }
  const { label, color } = cfg[status] ?? { label: status, color: '#64748b' }
  return (
    <span
      className="inline-flex items-center px-[7px] py-0.5 rounded-full text-[10px] font-[800] tracking-[0.06em] border border-transparent whitespace-nowrap"
      style={{ color, background: `${color}18`, borderColor: `${color}30` }}
    >
      {label}
    </span>
  )
}

// ── Run card ──────────────────────────────────────────────────────────────────

type RunCardProps = {
  run:       RunRecord
  isActive:  boolean
  agentName?: string
  onClick:   () => void
}

function RunCard({ run, isActive, agentName, onClick }: RunCardProps) {
  // Prefer the agent's name; fall back to the step's skill, then a short id.
  const label = agentName ?? primarySkill(run)
  const date  = formatRunDate(run.startedAt ?? run.queuedAt)

  return (
    <button
      className={[
        'flex flex-col gap-[6px] w-full text-left px-[14px] py-[10px] border-none border-b border-[rgba(148,163,184,0.06)] cursor-pointer transition-colors duration-[120ms] font-[inherit]',
        isActive
          ? 'bg-[rgba(59,130,246,0.08)] border-l-2 border-l-[#3b82f6] pl-[12px] hover:bg-[rgba(59,130,246,0.12)]'
          : 'bg-transparent hover:bg-[rgba(148,163,184,0.05)]',
      ].join(' ')}
      onClick={onClick}
      title={`Run ${run.id}`}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="text-[12.5px] font-bold text-[#c8d9ee] capitalize whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
          {label}
        </span>
        <span className="text-[10.5px] text-[#4e6480] flex-shrink-0 tabular-nums whitespace-nowrap">
          {date}
        </span>
      </div>
      <div className="flex items-center gap-[6px]">
        <StatusBadge status={run.status} />
      </div>
    </button>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export type RunHistorySidebarProps = {
  activeRunId: string
  onSelectRun: (runId: string) => void
}

export function RunHistorySidebar({ activeRunId, onSelectRun }: RunHistorySidebarProps) {
  const [isOpen,       setIsOpen]       = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  // agentId → name, resolved best-effort so cards can show names not UUIDs.
  const [agentNames, setAgentNames] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    api.get<{ items?: Array<{ id: string; name: string }> }>('/v1/agents')
      .then((res) => {
        if (cancelled) return
        const map: Record<string, string> = {}
        for (const a of res.items ?? []) map[a.id] = a.name
        setAgentNames(map)
      })
      .catch(() => { /* best-effort: fall back to skill label / short id */ })
    return () => { cancelled = true }
  }, [])

  const {
    historyRuns,
    historyNext,
    historyLoading,
    historyError,
    fetchHistory,
    setIsReadOnly,
  } = useObservabilityStore()

  // Build stable init headers once so fetchHistory can include auth.
  const initRef = useRef<RequestInit>({ headers: api.headers() })
  useEffect(() => { initRef.current = { headers: api.headers() } })

  // Initial load + re-fetch when status filter changes.
  useEffect(() => {
    void fetchHistory({
      status: statusFilter || undefined,
      append: false,
      init:   initRef.current,
    })
  }, [fetchHistory, statusFilter])

  const handleLoadMore = useCallback(() => {
    if (!historyNext || historyLoading) return
    void fetchHistory({
      cursor: historyNext,
      status: statusFilter || undefined,
      append: true,
      init:   initRef.current,
    })
  }, [fetchHistory, historyNext, historyLoading, statusFilter])

  const handleSelectRun = useCallback((run: RunRecord) => {
    const readOnly = run.status === 'succeeded' || run.status === 'failed' || run.status === 'cancelled'
    setIsReadOnly(readOnly)
    onSelectRun(run.id)
  }, [setIsReadOnly, onSelectRun])

  // ── Collapsed strip ───────────────────────────────────────────────────────

  if (!isOpen) {
    return (
      <aside
        data-testid="run-history-sidebar"
        data-state="collapsed"
        className="w-10 flex-shrink-0 h-full flex flex-col items-center bg-[rgba(10,16,24,0.96)] border-r border-[rgba(148,163,184,0.1)] pt-[14px]"
        aria-label="Run history (collapsed)"
      >
        <button
          className="flex flex-col items-center gap-2 pt-0.5 bg-transparent border-none cursor-pointer text-[#4e6480] hover:text-[#8fa3bd] transition-colors duration-[140ms] font-bold text-[12px] font-[inherit]"
          onClick={() => setIsOpen(true)}
          title="Open run history"
          aria-label="Open run history"
        >
          <span className="text-[10px]" aria-hidden="true"><ChevronRight size={14} /></span>
          <span
            className="text-[10px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] select-none"
            style={{ writingMode: 'vertical-rl' }}
          >
            History
          </span>
        </button>
      </aside>
    )
  }

  // ── Open panel ─────────────────────────────────────────────────────────────

  return (
    <aside
      data-testid="run-history-sidebar"
      data-state="expanded"
      className="w-[268px] flex-shrink-0 h-full flex flex-col bg-[rgba(10,16,24,0.96)] border-r border-[rgba(148,163,184,0.1)] overflow-hidden"
      aria-label="Run history"
    >
      {/* Header row */}
      <div className="flex items-center justify-between px-[14px] pt-[14px] pb-[10px] flex-shrink-0 border-b border-[rgba(148,163,184,0.08)]">
        <span className="text-[10.5px] font-[800] tracking-[0.14em] uppercase text-[#3a5068]">
          Run History
        </span>
        <button
          className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer text-[#4e6480] hover:text-[#8fa3bd] hover:bg-[rgba(148,163,184,0.08)] transition-colors duration-[140ms] text-[11px] font-bold px-[6px] py-1 rounded-[6px] font-[inherit]"
          onClick={() => setIsOpen(false)}
          title="Collapse run history"
          aria-label="Collapse run history"
        >
          <ChevronLeft size={14} />
        </button>
      </div>

      {/* Status filter */}
      <div className="px-3 pt-[10px] pb-2 flex-shrink-0">
        <select
          className="w-full bg-[rgba(6,10,16,0.7)] border border-[rgba(148,163,184,0.12)] rounded-lg text-[#8fa3bd] font-[inherit] text-[11.5px] px-[10px] py-[6px] outline-none cursor-pointer transition-colors duration-[140ms] focus:border-[rgba(59,130,246,0.4)]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="" className="bg-[#0d1520] text-[#c8d9ee]">All statuses</option>
          <option value="succeeded" className="bg-[#0d1520] text-[#c8d9ee]">Succeeded</option>
          <option value="failed" className="bg-[#0d1520] text-[#c8d9ee]">Failed</option>
          <option value="running" className="bg-[#0d1520] text-[#c8d9ee]">Running</option>
          <option value="waiting" className="bg-[#0d1520] text-[#c8d9ee]">Waiting</option>
          <option value="queued" className="bg-[#0d1520] text-[#c8d9ee]">Queued</option>
        </select>
      </div>

      {/* Run list */}
      <div
        className="flex-1 overflow-y-auto py-1 [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.15)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgba(148,163,184,0.15)] [&::-webkit-scrollbar-thumb]:rounded-sm"
        role="list"
      >
        {historyLoading && (historyRuns ?? []).length === 0 && (
          <div className="py-6 px-[14px] text-center flex justify-center items-center">
            <span className="rhs-spinner" aria-label="Loading…" />
          </div>
        )}

        {historyError && (
          <div className="mx-3 my-[10px] p-3 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-[10px] text-[#f87171] text-[11.5px] flex flex-col gap-2 items-start">
            <AlertTriangle size={14} aria-hidden="true" />
            {historyError}
            <button
              className="bg-transparent border-none text-[#f87171] font-[inherit] text-[11.5px] cursor-pointer underline p-0"
              onClick={() => void fetchHistory({ status: statusFilter || undefined, init: initRef.current })}
            >
              Retry
            </button>
          </div>
        )}

        {!historyLoading && !historyError && (historyRuns ?? []).length === 0 && (
          <div className="py-6 px-[14px] text-center text-[12px] text-[#3a5068] flex justify-center items-center">
            No runs found.
          </div>
        )}

        {(historyRuns ?? []).map((run) => (
          <div key={run.id} role="listitem">
            <RunCard
              run={run}
              isActive={run.id === activeRunId}
              agentName={run.agentId ? agentNames[run.agentId] : undefined}
              onClick={() => handleSelectRun(run)}
            />
          </div>
        ))}

        {/* Load-more / end-of-list */}
        {(historyRuns ?? []).length > 0 && (
          <div className="px-[14px] pt-[10px] pb-1 flex justify-center">
            {historyNext ? (
              <button
                className="inline-flex items-center justify-center gap-[6px] w-full bg-[rgba(148,163,184,0.07)] border border-[rgba(148,163,184,0.14)] rounded-lg text-[#8fa3bd] font-[inherit] text-[11.5px] font-bold px-[14px] py-[6px] cursor-pointer transition-colors duration-[130ms] hover:not-disabled:bg-[rgba(148,163,184,0.12)] hover:not-disabled:text-[#c8d9ee] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={historyLoading}
                onClick={handleLoadMore}
              >
                {historyLoading ? (
                  <><span className="rhs-spinner rhs-spinner--sm" aria-hidden="true" /> Loading…</>
                ) : (
                  'Load more'
                )}
              </button>
            ) : (
              <span className="text-[10.5px] text-[#3a5068] tabular-nums">End of history</span>
            )}
          </div>
        )}
      </div>
    </aside>
  )
}
